import React from 'react';
import { EquipmentHistoryTypes } from '../../../../../common/src/enums/EquipmentHistoryTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import * as htmlHelper from '../../../utils/htmlHelper';
import { SortColumn, SortDirections } from '../../../utils/sorting';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import { Modal } from '../../common/Modal/Modal';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelDatePicker,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelTextArea,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelLink,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortableHeader,
} from '../../elements';
import { Spinner } from '../../spinners';
import { ListPager } from '../../utils/ListPager';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { useReactAsync } from '../../utils/useReactAsync';
import { useSearch } from '../../utils/useSearch';

async function loadEquipmentHistory({
    equipmentId,
    query,
}: {
    equipmentId: string;
    query: { limit: string; offset: string; sortColumns: SortColumn[] };
}) {
    const result = await openApiFetchGet('/api/equipment/{equipmentId}/equipmentHistory/', { equipmentId, ...query });
    return result;
}

export function EquipmentHistory(props: { equipmentId: string }) {
    const translate = useTranslate();

    const { searchParameters, handleSortChanged, handleNavigation, queryObject, itemsPerPage } = useSearch(
        {},
        [{ columnName: 'date', direction: SortDirections.Descending }],
        10,
    );

    const { data, reload, isLoading } = useReactAsync(loadEquipmentHistory, { equipmentId: props.equipmentId, query: queryObject }, [
        props.equipmentId,
        queryObject,
    ]);

    const [isHistoryAddVisible, setIsHistoryAddVisible] = React.useState(false);

    async function removeEquipmentHistory(equipmentHistoryId: string) {
        await openApiFetchDelete('/api/equipmentHistory/{equipmentHistoryId}', { equipmentHistoryId });
        await reload();
    }

    async function saveEquipmentHistory(type: string, date?: string, description?: string) {
        await openApiFetchPost('/api/equipment/{equipmentId}/equipmentHistory/', { equipmentId: props.equipmentId }, { type, date, description });
        await reload();
    }

    const getAuthorFullName = (item: any) => {
        if (!item.user) {
            return '';
        }

        const firstName = item.user.firstName || '';
        const lastName = item.user.lastName || '';
        return `${firstName} ${lastName}`.trim();
    };

    return (
        <>
            <PanelHeading text={translate('common.history')} icon="fa fa-clock-o">
                {data?.items?.length ? (
                    <PanelLink
                        href={`/api/equipment/${props.equipmentId}/history/export`}
                        text={translate('survey.downloadExcelReport')}
                        icon="fa fa-download"
                        isDownload
                        small
                    />
                ) : (
                    <></>
                )}
                <ListPager
                    disabled={isLoading}
                    itemsPerPage={itemsPerPage}
                    inPanel={true}
                    total={data?.total ?? 0}
                    page={searchParameters.page}
                    handleNavigation={handleNavigation}
                />
            </PanelHeading>
            {(isLoading && <Spinner />) || (
                <>
                    <PanelBody noPadding>
                        <Table spaced={false}>
                            <TableHead>
                                <TableHeadRow light={true}>
                                    <TableSortableHeader
                                        header={translate('common.date')}
                                        columnName="date"
                                        sortColumns={searchParameters.sortColumns}
                                        onClick={handleSortChanged}
                                    />
                                    <TableSortableHeader
                                        header={translate('common.type')}
                                        columnName="type"
                                        sortColumns={searchParameters.sortColumns}
                                        onClick={handleSortChanged}
                                    />
                                    <TableHeader>{translate('common.description')}</TableHeader>
                                    <TableHeader>{translate('survey.author')}</TableHeader>
                                    <TableHeader>&nbsp;</TableHeader>
                                </TableHeadRow>
                            </TableHead>
                            <TableBody>
                                {data &&
                                    data.items.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableData nowrap>
                                                {' '}
                                                <NullableFormattedDate value={item.date ? item.date : item.created_at} />{' '}
                                            </TableData>
                                            <TableData>{translate(`equipment.${item.type}`)}</TableData>
                                            <TableData>{htmlHelper.newLineToBreak(item.description)}</TableData>
                                            <TableData>{getAuthorFullName(item)}</TableData>
                                            <TableButtons>
                                                <IconRemoveButton
                                                    removeQuestion={translate('common.confirmDelete', item.description)}
                                                    onClick={async () => removeEquipmentHistory(item.id)}
                                                />
                                            </TableButtons>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </PanelBody>
                    <EditPanelButtons footer>
                        <PanelButtonLink
                            onClick={() => setIsHistoryAddVisible(true)}
                            small={true}
                            text={translate('common.addItem', translate('common.history'))}
                            mt
                            primary
                            icon="fa fa-plus"
                        />
                    </EditPanelButtons>
                </>
            )}

            {isHistoryAddVisible && <HistoryAdd onClose={() => setIsHistoryAddVisible(false)} onSave={saveEquipmentHistory} />}
        </>
    );
}

function HistoryAdd(props: { onClose: () => void; onSave: (type: string, date?: string, text?: string) => void }) {
    const translate = useTranslate();
    const [historyType, setHistoryType] = React.useState<undefined | string>();
    const [description, setDescription] = React.useState<undefined | string>();
    const [historyDate, setHistoryDate] = React.useState<undefined | string>();

    const handleSaveClicked = () => {
        if (historyType) {
            props.onClose();
            props.onSave(historyType, historyDate, description);
        }
    };

    return (
        <Modal id="dialog" onClose={() => props.onClose()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('common.addItem', translate('common.history'))} </span>
                </h3>
            </Modal.Title>
            <Modal.Content style={{ height: '280px' }}>
                <div className="admin-form" style={{ width: '100%' }}>
                    <EditPanel>
                        <EditPanelRow>
                            <EditPanelCell rowCount={6}>
                                <EditPanelDatePicker
                                    name="from"
                                    value={historyDate}
                                    handleChange={(date) => {
                                        if (date) {
                                            setHistoryDate(date);
                                        }
                                    }}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelSection>
                            <EditPanelSelect
                                name="historyType"
                                value={historyType}
                                handleChange={(value) => setHistoryType(value)}
                                placeholder={translate('common.selectItem', translate('common.type'))}
                                options={[
                                    { value: EquipmentHistoryTypes.General, text: translate(`equipment.${EquipmentHistoryTypes.General}`) },
                                    { value: EquipmentHistoryTypes.Incident, text: translate(`equipment.${EquipmentHistoryTypes.Incident}`) },
                                    { value: EquipmentHistoryTypes.Modification, text: translate(`equipment.${EquipmentHistoryTypes.Modification}`) },
                                    { value: EquipmentHistoryTypes.Repair, text: translate(`equipment.${EquipmentHistoryTypes.Repair}`) },
                                ]}
                            />
                        </EditPanelSection>
                        <EditPanelTextArea value={description} handleChange={(value) => setDescription(value)} name="description" />
                    </EditPanel>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.close')} onClick={() => props.onClose()} />
                    <EditPanelButton small={true} text={translate('common.save')} onClick={() => handleSaveClicked()} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}
